import {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import useListActions from 'web/modules/appearance-management/hooks/useList';
import {editCase, fetchCase} from 'web/redux/actions/CaseActions';
import {
  fetchCustomer,
  fetchCustomerSelection,
} from 'web/redux/actions/CustomerActions';
import {fetchRelCustomerMachineSelection} from 'web/redux/actions/RelCustomerMachineActions';
import {formatDate} from 'web/utils/dateHelper';
import enumList from 'web/utils/enumList';
import moment from 'moment-timezone';
import store from 'web/redux/store';
import axios from 'axios';

export const useCaseDetail = (
  dispatch,
  showMessage,
  setSelectedCustomerData,
  caseNumber,
) => {
  const BASE_URL = process.env.BASE_URL;
  const ATTACHMENT_PATH = `${BASE_URL}/attachment`;
  const navigate = useNavigate();
  const conditionList = useSelector(
    (state) => state.condition.conditionList.data,
  );
  const [casePriorityDisplay, setCasePriorityDisplay] = useState('');
  const [customerData, setCustomerData] = useState([]);
  const [caseDetailFiles, setCaseDetailFiles] = useState([]);
  const [customerSelection, setCustomerSelection] = useState([]);
  const [appearanceSelection, setAppearanceSelection] = useState([]);
  const [appearances, setAppearances] = useState([]);
  const [reportAppearances, setReportAppearances] = useState(new Map());
  const [conditions, setConditions] = useState([]);
  const [relCustomerMachineSelection, setRelCustomerMachineSelection] =
    useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [assignments, setAssignments] = useState(new Map());
  const [isRequiredAppearances, setIsRequiredAppearances] = useState(false);

  const [formValues, setFormValues] = useState({
    casePriority: '',
    caseType: '',
    caseNumber: '',
    createTime: moment().format('YYYY-MM-DD'),
    customerName: '',
    customerCode: '',
    description: '',
    purpose: '',
    flgStatus: '',
  });
  const {fetchAppearances} = useListActions();

  const getCaseByCaseNumber = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchCase({caseNumber, withAssignmentReport: true}),
      );
      const statusCode = response.payload.code;
      if (statusCode.isSuccessCode()) {
        const caseData = response.payload.result;
        const date = formatDate(caseData.createTime);
        setFormValues({
          casePriority: caseData.casePriority,
          caseType: caseData.caseType,
          caseNumber: caseData.caseNumber,
          createTime: date,
          customerName: caseData.customerName,
          customerCode: caseData.customerCode,
          description: caseData.description,
          purpose: caseData.purpose,
          flgStatus: caseData.flgStatus,
        });
        const conditionCodes = caseData.conditions.map(
          (item) => item.conditionCode,
        );

        setConditions(conditionCodes);
        setAppearances(caseData.appearances ?? []);
        setCasePriorityDisplay(caseData.casePriority);
        setCustomerData([
          {
            customerName: caseData.customerName,
            customerCode: caseData.customerCode,
          },
        ]);
        setCaseDetailFiles(caseData.uploadFiles);
        setSelectedMachines(
          caseData.assignmentModels.map(({machineId}) => machineId),
        );
        setAssignments(
          new Map(
            caseData.assignmentModels.map((assignment) => {
              return [assignment.machineId, assignment];
            }),
          ),
        );
        setReportAppearances(
          new Map(
            caseData?.assignmentReportModels
              .filter((item) => item && item.appearance)
              .map(({assignment_id, appearance}) => [
                assignment_id,
                appearance,
              ]),
          ),
        );
        const caseType = parseInt(caseData.caseType);
        const requiredCaseTypes = [
          enumList.CaseTypeEnums.repair.value,
          enumList.CaseTypeEnums.maintenance.value,
        ];
        if (requiredCaseTypes.includes(caseType)) {
          return setIsRequiredAppearances(true);
        }
      } else {
        showMessage(
          enumList.messageModalMode.error,
          response.payload.code,
          response.payload.message,
        );
        console.log('not success:');
      }
    } catch (error) {
      console.error('error:', error);
    }
  }, [caseNumber]);

  const getCustomerSelection = async () => {
    try {
      const response = await dispatch(fetchCustomerSelection());
      const statusCode = response.payload.code;
      if (statusCode.isSuccessCode()) {
        const customerSelection = response.payload.result;
        setCustomerSelection(customerSelection);
      } else {
        console.log('not success:');
      }
    } catch (error) {
      console.error('error:', error);
    }
  };

  const handleEditCase = async (caseData, files) => {
    try {
      const response = await dispatch(editCase({caseData, files}));
      const statusCode = response.payload.code;
      if (statusCode.isSuccessCode()) {
        showMessage(enumList.messageModalMode.info, '更新成功');
        getCaseByCaseNumber();
      } else {
        console.log('not success:');
        showMessage(
          enumList.messageModalMode.error,
          response.payload.code,
          response.payload.message,
        );
      }
    } catch (error) {
      console.error('error:', error);
      showMessage(
        enumList.messageModalMode.error,
        error?.response?.payload?.code,
        error?.response?.payload?.message,
      );
    }
  };

  const getRelCustomerMachineSelection = useCallback(async () => {
    if (!customerData?.[0]?.customerCode) {
      return;
    }
    try {
      const response = await dispatch(
        fetchRelCustomerMachineSelection({
          customerCode: customerData[0].customerCode,
          isEnabled: true,
        }),
      );
      const statusCode = response.payload.code;
      if (statusCode.isSuccessCode()) {
        const machines = response.payload.result;
        const sortedMachines = machines.toSorted(
          (a, b) => a.machineTypeId - b.machineTypeId,
        );
        setRelCustomerMachineSelection(sortedMachines);
      } else {
        console.log('not success:');
      }
    } catch (error) {
      console.error('error:', error);
    }
  }, [customerData]);

  const goBack = () => {
    navigate(-1);
  };

  const checkCoordinatesInfo = async (customerCode) => {
    try {
      const res = await dispatch(fetchCustomer(customerCode)).unwrap();
      if (res.code.isSuccessCode()) {
        const {latitude, longitude} = res.result;
        if (!latitude || !longitude) {
          setSelectedCustomerData(res.result);
          return false;
        }
        return true;
      }
    } catch (error) {
      console.error('error:', error);
    }
  };

  const getAppearanceList = async () => {
    try {
      const res = await fetchAppearances({
        filters: {withCondition: true, flgStatus: enumList.caseFlgStatus.open},
      });
      setAppearanceSelection(res);
    } catch (error) {
      console.error(error?.response?.data);
    }
  };

  const handleDownloadFile = async (fileName) => {
    console.log('下載檔名：', fileName);
    const filePath = `${ATTACHMENT_PATH}/${fileName}`;

    const file = await getFile(filePath);
    console.log(file);

    if (!file) {
      console.error('檔案取得失敗');
      return showMessage(
        enumList.messageModalMode.error,
        '檔案取得失敗，請稍後再試',
      );
    }

    const blob = new Blob([file.data], {type: file.headers['content-type']});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };
  const getFile = async (path) => {
    try {
      const token = store.getState().auth.token;
      return await axios.get(path, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('error:', error);
      showMessage(
        enumList.messageModalMode.error,
        error?.response?.data?.payload?.message || '發生錯誤',
      );
    }
  };

  return {
    casePriorityDisplay,
    customerData,
    formValues,
    caseDetailFiles,
    customerSelection,
    conditions,
    appearances,
    selectedMachines,
    setSelectedMachines,
    reportAppearances,
    relCustomerMachineSelection,
    appearanceSelection,
    conditionList,
    assignments,
    setCaseDetailFiles,
    setCustomerSelection,
    setCasePriorityDisplay,
    setAppearances,
    setCustomerData,
    setFormValues,
    setConditions,
    getCaseByCaseNumber,
    getCustomerSelection,
    getRelCustomerMachineSelection,
    handleEditCase,
    goBack,
    checkCoordinatesInfo,
    getAppearanceList,
    handleDownloadFile,
    isRequiredAppearances,
  };
};
